import type { UseMutationResult } from '@tanstack/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { COMMUNITY_SECURE_GLOBAL_PREFIX } from 'shared/api/constants/api-constants'
import { useApiWrapperWithErrorValidation } from 'shared/hooks/use-api-wrapper-with-error-validation'
import { RequestMethodsEnum } from 'shared/hooks/use-api-wrapper-with-error-validation/types'
import type { ReportRequest } from '../types/moderation'
import { communityPostKeys, communityReportKeys } from './key-factory'

export const useReportPost = ({
  communityPath,
  postId,
  onSuccess,
  onError,
}: {
  communityPath: string
  postId: number
  onSuccess?: () => void
  onError?: (error: Error) => void
}): UseMutationResult<void, Error, ReportRequest> => {
  const queryClient = useQueryClient()

  const { fetcher } = useApiWrapperWithErrorValidation<
    RequestMethodsEnum.post,
    void,
    ReportRequest
  >({
    method: RequestMethodsEnum.post,
    badRequestHandler: () => {},
    notFoundResponseHandler: () => {},
    accessDeniedResponseHandler: () => {},
    unprocessableContentHandler: () => {},
    conflictResponseHandler: () => {},
  })

  return useMutation<void, Error, ReportRequest>({
    mutationFn: body =>
      fetcher(`${COMMUNITY_SECURE_GLOBAL_PREFIX}/${communityPath}/report/post/${postId}`, body),

    onSuccess: onSuccess ? () => onSuccess() : undefined,

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: communityPostKeys.all })
      void queryClient.invalidateQueries({ queryKey: communityReportKeys.all })
    },

    onError: onError ? error => onError(error) : undefined,
  })
}
